import { useMemo, useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import { FaCheckCircle, FaDownload, FaSpinner } from "react-icons/fa";
import PageLoading from "components/PageLoading";
import { sortHistoricoDocumentacaoDesc } from "utils/date";
import { downloadExtratoById, downloadExtratoByArquivoId } from "../../../../services/ExtractService";
import type { ExtractHistoryEntry } from "../shared/extractListTypes";
import "../../Analysis/ComponentsAnalysis/DocumentationHistory/DocumentationHistory.css";
import "./HistoryExtract.css";

export type HistoryExtractItem = ExtractHistoryEntry;

export interface HistoryExtractProps {
  show: boolean;
  onHide: () => void;
  historyDataExtract: HistoryExtractItem[];
  loading?: boolean;
}

type HistoryRowView = {
  acao: string;
  data: string;
  id?: string;
  source: HistoryExtractItem;
};

const HistoryExtract = ({
  show,
  onHide,
  historyDataExtract = [],
  loading = false,
}: HistoryExtractProps) => {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const historyOrdenado = useMemo(() => {
    const rows: HistoryRowView[] = (historyDataExtract || []).map((item) => ({
      acao: item.acao || item.name || item.nome || "-",
      data: item.data || item.dataHoraBrasilia || item.dataHora || "-",
      id: item.idExtrato,
      source: item,
    }));
    return sortHistoricoDocumentacaoDesc(rows);
  }, [historyDataExtract]);

  const rowKey = (item: HistoryExtractItem, index: number) =>
    `${item.idExtrato ?? ""}-${item.dataUpload ?? ""}-${item.identificadorBlob ?? ""}-${index}`;

  const handleDownload = async (item: HistoryExtractItem, key: string) => {
    setLoadingKey(key);

    try {
      let res: { url: string; filename: string; blob: Blob } | null = null;

      if (item?.arquivoId != null) {
        res = await downloadExtratoByArquivoId(item.arquivoId);
      } else {
        const identificadorBlob = String(item?.identificadorBlob || "").trim();
        if (!identificadorBlob) {
          console.warn("Identificador do blob não informado para download.", item);
          return;
        }
        res = await downloadExtratoById(identificadorBlob);
      }

      if (!res) {
        console.warn("Não foi possível baixar o extrato.");
        return;
      }

      let url = res.url;

      if (!url && res.blob) {
        url = URL.createObjectURL(res.blob);
      }

      if (!url) {
        console.warn("URL do arquivo não foi gerada.");
        return;
      }

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", res.filename || `extrato_${key}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (res.blob) {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Erro ao baixar extrato:", err);
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="doc-history-modal doc-history-modal--extrato"
    >
      <Modal.Header closeButton>
        <Modal.Title className="modal-title">HISTÓRICO DO EXTRATO</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="doc-history-modal-scroll-pane doc-history-modal-scroll-pane--center">
            <PageLoading minHeight="200px" message="Carregando histórico..." />
          </div>
        ) : historyOrdenado.length === 0 ? (
          <div className="doc-history-modal-scroll-pane doc-history-modal-scroll-pane--center text-center py-3">
            <p className="mb-0">Nenhum histórico de extrato encontrado.</p>
          </div>
        ) : (
          <div className="doc-history-modal-scroll-pane">
            <Table bordered className="doc-history-table doc-history-table--extrato mb-0">
              <thead>
                <tr>
                  <th>AÇÃO</th>
                  <th>DATA/HORA</th>
                  <th>EXTRATO</th>
                </tr>
              </thead>
              <tbody>
                {historyOrdenado.map((row, index) => {
                  const item = row.source;
                  const key = rowKey(item, index);
                  const isDownloading = loadingKey === key;
                  const hasBlobId =
                    Boolean(String(item?.identificadorBlob || "").trim()) ||
                    item?.arquivoId != null;

                  return (
                    <tr
                      key={
                        row.id != null
                          ? `h-${String(row.id)}`
                          : `${row.acao}-${row.data}-${index}`
                      }
                    >
                      <td>
                        <FaCheckCircle className="icon-check" /> {row.acao}
                      </td>
                      <td>{row.data}</td>
                      <td className="extract-history-extrato-cell">
                        {hasBlobId ? (
                          <button
                            type="button"
                            className="extract-download-link btn btn-link p-0"
                            title="Baixar extrato"
                            disabled={isDownloading}
                            aria-busy={isDownloading}
                            onClick={(e) => {
                              e.preventDefault();
                              handleDownload(item, key);
                            }}
                          >
                            {isDownloading ? (
                              <FaSpinner className="icon-loading spin" />
                            ) : (
                              <FaDownload className="icon-download" />
                            )}{" "}
                            Baixar extrato
                          </button>
                        ) : (
                          <span
                            className="text-muted"
                            title="Sem identificador do extrato para download"
                            aria-disabled="true"
                          >
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        <div className="doc-history-modal-footer text-center">
          <Button className="btn-ok" onClick={onHide}>
            OK
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default HistoryExtract;
